import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { addSearch, fetchSavedSearches } from '../../actions/savedSearches';
import { SavedSearchModal } from '../search/results-ts';
import { findExistingAlert } from '../../utils/alertComparison';
import i18n from '../../i18n';
import UserModel from '../../models/User';
import Analytics from '../../utils/analytics';
import { isMapAreaFilters } from '../../utils/mapAreaFilters';
import { searchResultsData, trackButtonClick } from '../../utils/utils-ts';
import Utils from '../../utils/utils';
import {
    AlertDialog,
    Button as PanoramaButton,
    Icon as PanoramaIcon,
    Loader as PanoramaLoader
} from '@homeexchange/panorama';
import { IconBellAnimated } from '../icons/IconBellAnimated';
import Screen from '../../utils/Screen';

export class Alert extends React.PureComponent {
    static propTypes = {
        user: PropTypes.instanceOf(UserModel),
        isLogged: PropTypes.bool,
        alerts: PropTypes.arrayOf(PropTypes.object),
        search: PropTypes.object.isRequired,
        iconOnly: PropTypes.bool,
        trackingOrigin: PropTypes.string,
        groups: PropTypes.array,
        resultsBounds: PropTypes.object,
        addAlert: PropTypes.func,
        fetchSavedSearches: PropTypes.func,
        currentFilters: PropTypes.object,
        currentQueryString: PropTypes.string
    };

    constructor(props) {
        super(props);
        const { alerts, search } = props;
        this.state = {
            success: false,
            errorPopup: false,
            alert: this.getExistingAlert(alerts, search),
            createAlert: null,
            isOpen: false,
            isSaving: false,
            openModalAlertAlreadyExists: false
        };
        this.saveSearch = this.saveSearch.bind(this);
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        const { alerts, search } = nextProps;
        this.setState({
            alert: this.getExistingAlert(alerts, search)
        });
    }

    getExistingAlert(alerts, search) {
        return findExistingAlert(alerts, search);
    }

    saveSearch(event) {
        event.preventDefault();

        const { search, user, isLogged } = this.props;
        const { alert, isSaving } = this.state;

        if (!user && !isLogged) {
            $('.btn-signup').trigger('click');
            return;
        }
        if (isSaving) {
            return;
        }

        Analytics.trackGTM('SearchResults', {
            event_data: {
                action: 'click',
                area: 'search',
                text: isMapAreaFilters(search.filters)
                    ? searchResultsData.text.saveMapArea
                    : searchResultsData.text.saveDestination,
                origin: this.props.trackingOrigin || searchResultsData.origin.topMenu,
                search_value: search.query
            }
        });

        if (alert) {
            this.setState({
                openModalAlertAlreadyExists: true
            });
        } else {
            const filtersToSave = this.props.currentFilters || {};
            const queryString = this.props.currentQueryString || search.query;
            this.setState({ isSaving: true });
            Promise.resolve(this.props.addAlert(queryString, filtersToSave))
                .then((response) => {
                    this.setState({
                        createAlert: response,
                        isOpen: Boolean(response)
                    });
                    this.props.fetchSavedSearches();
                })
                .catch(() => this.setState({ createAlert: null, isOpen: false }))
                .finally(() => this.setState({ isSaving: false }));
        }
    }

    trackAlreadySaved(text) {
        trackButtonClick('saved_search_already_saved_modal', text, Boolean(Utils.isCollectionWebSite()));
    }

    renderBell() {
        if (Screen.matchesMedia('(prefers-reduced-motion: reduce)')) {
            return <PanoramaIcon name="Bell" className="save-destination-icon" />;
        }
        return <IconBellAnimated />;
    }

    renderButton() {
        const { alert, isSaving } = this.state;
        const stateClass = (current) => `save-destination-state${current && !isSaving ? ' is-current' : ''}`;

        // Both labels share one grid cell so the button keeps the width of the longer one in every
        // state; only the content fades, and the loader is overlaid in the middle.
        return (
            <PanoramaButton
                className="save-destination-button"
                type="button"
                aria-busy={isSaving}
                onClick={this.saveSearch}
                {...(alert
                    ? { isActive: true, hierarchy: 'secondary', size: 'medium' }
                    : { hierarchy: 'secondary', size: 'medium' })}
            >
                <span className="save-destination-states">
                    <span className={stateClass(!alert)} aria-hidden={Boolean(alert) || isSaving}>
                        {this.renderBell()}
                        {i18n.t('search:alert.save_search')}
                    </span>
                    <span className={stateClass(Boolean(alert))} aria-hidden={!alert || isSaving}>
                        <PanoramaIcon name="BellOff" className="save-destination-icon" />
                        {i18n.t('search:alert_search_already_saved')}
                    </span>
                </span>
                {isSaving && <PanoramaLoader size="small" className="save-destination-loader" />}
            </PanoramaButton>
        );
    }

    render() {
        const { user, isLogged } = this.props;

        if (!user && !isLogged) {
            return null;
        }

        const { query, filters } = this.props.search;
        const isMapArea = Boolean(filters?.location?.bounds);
        if ((!query || query.length === 0) && !isMapArea) {
            return null;
        }

        return (
            <>
                {this.renderButton()}
                <SavedSearchModal
                    isOpen={this.state.isOpen}
                    onClose={() => this.setState({ isOpen: false })}
                    createdAlert={this.state.createAlert}
                    bounds={filters?.location?.bounds || this.props.resultsBounds}
                    filters={filters}
                    groups={this.props.groups}
                    onDeleted={() => {
                        this.setState({ createAlert: null, alert: null });
                        this.props.fetchSavedSearches();
                    }}
                />
                <AlertDialog
                    isOpen={this.state.openModalAlertAlreadyExists}
                    title={i18n.t('user:modal_travelWishlist_alreadySaved_title')}
                    description={i18n.t('user:modal_travelWishlist_alreadySaved_body')}
                    confirmAction={{
                        label: i18n.t('user:modal_travelWishlist_alreadySaved_cta'),
                        onClick: () => {
                            this.trackAlreadySaved('see_my_travel_wishlist');
                            window.location.href = i18n.t('url:saved_search');
                        }
                    }}
                    cancelAction={{
                        label: i18n.t('common:cancel'),
                        onClick: () => {
                            this.trackAlreadySaved('cancel');
                            this.setState({ openModalAlertAlreadyExists: false });
                        }
                    }}
                />
            </>
        );
    }
}

const mapStateToProps = (state) => ({
    user: state.user,
    isLogged: Boolean(state.auth?.accessToken),
    homes: state.homes,
    alerts: state.savedSearches,
    currentFilters: state.search?.main?.filters,
    currentQueryString: state.search?.main?.queryString
});

const mapDispatchToProps = (dispatch) => ({
    addAlert: bindActionCreators(addSearch, dispatch),
    fetchSavedSearches: bindActionCreators(fetchSavedSearches, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Alert);
